import { useMemo } from 'react';
import type {
  CrawlJob,
  CrawlJobStatusDTO,
  WithResourceId,
} from '../../../types/knowledge';
import {
  CRAWLS_IN_PROGRESS_GROUP_STATUSES,
  CRAWLS_MAPPING_GROUP_STATUSES,
  getEffectiveCrawlStatus,
  isAbandonedCrawl,
  NEEDS_ATTENTION_CRAWL_STATUSES,
} from '../knowledgePolling';

export function groupCrawlsByStatus(
  crawls: Array<WithResourceId<CrawlJob>>,
  liveStatusByCrawlId: Map<string, CrawlJobStatusDTO>,
) {
  const crawlsMapping = crawls.filter((crawl) => {
    const liveStatus = liveStatusByCrawlId.get(crawl.id);
    if (isAbandonedCrawl(crawl, liveStatus)) {
      return false;
    }
    const status = getEffectiveCrawlStatus(crawl, liveStatus);
    return status != null && CRAWLS_MAPPING_GROUP_STATUSES.has(status);
  });

  const crawlsNeedingAttention = crawls.filter((crawl) => {
    const liveStatus = liveStatusByCrawlId.get(crawl.id);
    if (isAbandonedCrawl(crawl, liveStatus)) {
      return true;
    }
    const status = getEffectiveCrawlStatus(crawl, liveStatus);
    return status != null && NEEDS_ATTENTION_CRAWL_STATUSES.has(status);
  });

  const crawlsInProgress = crawls.filter((crawl) => {
    const liveStatus = liveStatusByCrawlId.get(crawl.id);
    if (isAbandonedCrawl(crawl, liveStatus)) {
      return false;
    }
    const status = getEffectiveCrawlStatus(crawl, liveStatus);
    return status != null && CRAWLS_IN_PROGRESS_GROUP_STATUSES.has(status);
  });

  const crawlsCompleted = crawls.filter((crawl) => {
    const liveStatus = liveStatusByCrawlId.get(crawl.id);
    const status = getEffectiveCrawlStatus(crawl, liveStatus);
    return status === 'COMPLETED' && !isAbandonedCrawl(crawl, liveStatus);
  });

  const crawlsFailed = crawls.filter((crawl) => {
    const liveStatus = liveStatusByCrawlId.get(crawl.id);
    const status = getEffectiveCrawlStatus(crawl, liveStatus);
    return status === 'FAILED' && !isAbandonedCrawl(crawl, liveStatus);
  });

  return {
    crawlsMapping,
    crawlsNeedingAttention,
    crawlsInProgress,
    crawlsCompleted,
    crawlsFailed,
  };
}

export function useGroupedCrawlsByStatus(
  crawls: Array<WithResourceId<CrawlJob>>,
  liveStatusByCrawlId: Map<string, CrawlJobStatusDTO>,
) {
  return useMemo(
    () => groupCrawlsByStatus(crawls, liveStatusByCrawlId),
    [crawls, liveStatusByCrawlId],
  );
}
